import React from 'react';
import { Settings, BarChart2, FileCheck, Building } from 'lucide-react';

/**
 * Компонент секции настроек для формы расчета ликвидности
 */
const SettingsSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  industryTypes,
  companySizes
}) => {
  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Settings className="w-5 h-5 mr-2 text-gray-600 dark:text-gray-400" />
        Настройки анализа
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Отрасль */}
        <div>
          <label htmlFor="settings.industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Отрасль
          </label>
          <select
            id="settings.industry"
            name="settings.industry"
            value={formData.settings.industry}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {industryTypes.map((industry) => (
              <option key={industry.id} value={industry.id}>
                {industry.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Для сравнения с отраслевыми нормативами
          </p>
        </div>

        {/* Размер компании */}
        <div>
          <label htmlFor="settings.companySize" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Размер компании
          </label>
          <select
            id="settings.companySize"
            name="settings.companySize"
            value={formData.settings.companySize}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {companySizes.map((size) => (
              <option key={size.id} value={size.id}>
                {size.name} (до {size.revenueLimit})
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Влияет на интерпретацию результатов
          </p>
        </div>

        {/* Стандарт отчетности */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Стандарт отчетности
          </label>
          <div className="mt-1 flex items-center space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="settings.accountingStandard"
                value="ras"
                checked={formData.settings.accountingStandard === 'ras'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                РСБУ
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="settings.accountingStandard"
                value="ifrs"
                checked={formData.settings.accountingStandard === 'ifrs'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                МСФО
              </span>
            </label>
          </div>
        </div>

        {/* Период сравнения */}
        <div>
          <label htmlFor="settings.comparisonPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Период для сравнения
          </label>
          <select
            id="settings.comparisonPeriod"
            name="settings.comparisonPeriod"
            value={formData.settings.comparisonPeriod}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="current">Текущий</option>
            <option value="previous">Предыдущий</option>
            <option value="custom">Пользовательский</option>
          </select>
        </div>
        
        {/* Пользовательское название периода */}
        {formData.settings.comparisonPeriod === 'custom' && (
          <div>
            <label htmlFor="settings.customPeriodName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Название периода
            </label>
            <input
              type="text"
              id="settings.customPeriodName"
              name="settings.customPeriodName"
              value={formData.settings.customPeriodName}
              onChange={handleInputChange}
              className="form-input w-full"
              placeholder="Например: Плановый период"
            />
          </div>
        )}
      </div>

      {/* Дополнительные опции */}
      <div className="mt-6 space-y-3">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
          <BarChart2 className="w-4 h-4 mr-1.5 text-indigo-500" />
          Дополнительные параметры
        </h4>
        
        <div className="flex flex-col gap-3">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="settings.includeDetailedAssets"
              checked={formData.settings.includeDetailedAssets}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Детализировать оборотные активы
            </span>
          </label>
          
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="settings.includeDetailedLiabilities"
              checked={formData.settings.includeDetailedLiabilities}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Детализировать краткосрочные обязательства
            </span>
          </label>
          
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              name="settings.includeBankruptcyRiskAssessment"
              checked={formData.settings.includeBankruptcyRiskAssessment}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включить оценку риска банкротства
            </span>
          </label>
        </div>
      </div>

      {/* Информационная подсказка */}
      <div className="mt-4 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <FileCheck className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-indigo-800 dark:text-indigo-200">
              Важно знать о стандартах отчетности
            </h4>
            <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
              В России используются два основных стандарта: РСБУ (российские) и МСФО (международные). Нормативы ликвидности могут отличаться в зависимости от используемого стандарта. При работе с МСФО более корректно сравнивать показатели с международными бенчмарками.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsSection;